import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { Observable, Subscription } from 'rxjs';
import { of } from 'rxjs/observable/of';
import { ToasterService } from 'angular2-toaster';
import { Location } from '@angular/common';
import { map, catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '../../../services/auth.service';
import { ProjectManagerService } from '../../../services/project-manager.service';
import { ModalStartScreenComponent } from '../modal-start-screen/modal-start-screen.component';
import { LoadingSpinnerService } from '../../../services/loading-spinner.service';
import { ProjectSaveService } from '../../../services/project-save.service';
import { projectNameNotEmpty } from './form-validators/project-name-not-empty';
import { projectNameHasNotSpecialCharacters } from './form-validators/project-name-has-not-special-characters';
import { projectNameValid } from './form-validators/project-name-valid';

@Component({
  selector: 'app-modal-save',
  templateUrl: './modal-save.component.html',
  styleUrls: ['./modal-save.component.scss']
})
export class ModalSaveComponent implements OnInit {
  form: FormGroup;
  projectName: string;
  isSaved: boolean;
  isAuthorization: boolean = true;
  saveAsNewProject = false;
  saveInProgress = false;
  admin = false;
  initSubscription: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ModalSaveComponent>,
    private auth: AuthService,
    private projectManagerService: ProjectManagerService,
    private toasterService: ToasterService,
    private location: Location,
    private loadingSpinnerService: LoadingSpinnerService,
    private saveProjectService: ProjectSaveService,
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    this.loadingSpinnerService.loaderActive.subscribe(value => {
      this.saveInProgress = value;
    });
  }

  private _showToast(type: string, title: string, body: string) {
    this.toasterService.popAsync(type, title, body);
  }

  private _projectExists() {
    if (this.projectManagerService.project.id) {
      // exists: true
      return this.projectManagerService
        .projectExists(this.projectManagerService.project.id)
        .pipe(map(res => res.exists));
    } else {
      return of(false);
    }
  }

  private _getProjectName() {
    if (
      this.projectManagerService.project.projectName &&
      !this.projectManagerService.ifCurrentProjIsSharedProj(this.projectManagerService.project.id)
    ) {
      return of(this.projectManagerService.project.projectName);
    } else {
      return this.projectManagerService.getNewProjectName();
    }
  }

  private _createForm(projectExist, projectName = '') {
    let saveAsNewProject = false;
    if (
      this.projectManagerService.ifCurrentProjIsSharedProj(this.projectManagerService.project.id)
    ) {
      saveAsNewProject = true;
    }

    if (
      !this.projectManagerService.project.id ||
      (this.projectManagerService.project.id && !projectExist)
    ) {
      saveAsNewProject = true;
    }

    this.form = this.fb.group(
      {
        projectName: [projectName, [projectNameNotEmpty(), projectNameHasNotSpecialCharacters()]],
        saveAsNewProject: saveAsNewProject
      },
      {
        asyncValidator: projectNameValid(
          this.projectManagerService.projectNameValid.bind(this.projectManagerService)
        )
      }
    );
  }

  private _setProjectNameAfterAuthorization() {
    return this.auth.isAuthorization.pipe(
      switchMap(isAuthorization => {
        this.isAuthorization = isAuthorization;
        if (isAuthorization) {
          return this._getProjectName().pipe(
            map(projectName => {
              this.form.get('projectName').setValue(projectName);
            })
          );
        } else {
          return of(null);
        }
      })
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }

  onSubmit() {
    this.loadingSpinnerService.spinnerLoadingListAdd('saveProjectInModalSaveComponent');
    const projectName = this.form.get('projectName').value;
    const saveAsNewProject = this.form.get('saveAsNewProject').value;
    if (this.projectManagerService.ifCurrentProjIsSharedProj(this.projectManagerService.project.id))
      this.location.replaceState('');

    this.saveProjectService.save(projectName, saveAsNewProject).subscribe(
      () => {
        this.loadingSpinnerService.spinnerLoadingListRemove('saveProjectInModalSaveComponent');
        this.dialogRef.close();
      },
      err => {
        this.loadingSpinnerService.spinnerLoadingListRemove('saveProjectInModalSaveComponent');
        const message =
          typeof err === 'string'
            ? err
            : (err && err.message) || 'Cannot save project. Please try again.';
        this._showToast('error', 'Save error', message);
      }
    );
  }

  login() {
    this.dialogRef.close();
    this.auth.triggerAuthToSave();
    let dialogRef = this.dialog.open(ModalStartScreenComponent, {
      width: '835px',
      height: '75vh',
      data: 'login'
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap(() => {
          return this._setProjectNameAfterAuthorization();
        })
      )
      .subscribe();
  }

  register() {
    this.dialogRef.close();

    let dialogRef = this.dialog.open(ModalStartScreenComponent, {
      width: '835px',
      height: '75vh',
      data: 'register'
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap(() => {
          return this._setProjectNameAfterAuthorization();
        })
      )
      .subscribe();
  }

  ngOnInit() {
    console.log('..initializing modal-save-component');
    // get project unique name...
    this.initSubscription = combineLatest(
      this.auth.isAuthorization,
      this.projectManagerService.isSavedProject,
      this._projectExists()
    )
      .pipe(
        switchMap(([isAuthorization, isSavedProject, projectExists]) => {
          this.isAuthorization = isAuthorization;
          this.isSaved = isSavedProject;
          this.admin = this.authService.getRole() === 'admin';
          this._createForm(projectExists);
          if (isAuthorization) {
            return this._getProjectName().pipe(
              map(projectName => {
                this.form.get('projectName').setValue(projectName);
              })
            );
          } else {
            return of(true);
          }
        }),
        catchError(err => {
          this.closeDialog();
          this._showToast('error', 'Error', 'Cannot open save dialog. Please, try later');
          return of(err);
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.initSubscription) this.initSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/modals/modal-save/modal-save.component.ts